import React, { useRef, useState, useEffect } from "react";
import styles from "../css/CardHSPUProgram.module.css";
import headerImage from "../assets/img/icon_hspu_white.png";
import html2canvas from "html2canvas";
import { FaDownload } from "react-icons/fa";
import { BsFillPlayBtnFill } from "react-icons/bs";
import data_program from '../data/data_program';
import badge1 from '../assets/img/badges/1_badge_rabbit.png';
import badge2 from '../assets/img/badges/2_badge_deer.png';
import badge3 from '../assets/img/badges/3_badge_wolf.png';
import badge4 from '../assets/img/badges/4_badge_horse.png';
import badge5 from '../assets/img/badges/5_badge_bear.png';
import badge6 from '../assets/img/badges/6_badge_gorilla.png';
import badge7 from '../assets/img/badges/7_badge_lion.png';
import ModalVideo from "./ModalVideo"; // adjust path based on your structure
import { fetchPrioritizedVideos } from "./dynamoUtils"; // ✅ same as your other imports
import { toYouTubeEmbed, looksLikeVideoFilePath } from "./dynamoUtils";


function CardHSPUProgram() {
  const cardRef = useRef(null);
  const [showVideo, setShowVideo] = useState(false);
  const [carouselVideos, setCarouselVideos] = useState([]);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [currentTitle, setCurrentTitle] = useState("");

  const levelBadges = {
    "1": badge1,
    "2": badge2,
    "3": badge3,
    "4": badge4,
    "5": badge5,
    "6": badge6,
    "7": badge7,
  };

  // ---- video helpers (new + improved) ----
  const handleCapture = () => {
    if (cardRef.current) {
      html2canvas(cardRef.current, {
        scale: 4,
        backgroundColor: 'transparent' // Set the background to transparent
      }).then((canvas) => {
        // Convert the canvas to a data URL and create a download link
        const dataURL = canvas.toDataURL("image/png");
        const a = document.createElement("a");
        a.href = dataURL;
        a.download = "thenics4all_hspu_program.png";
        a.click();
      });
    }
  };

  // Add an event listener to close the modal when clicking outside of it
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (showVideo && cardRef.current && !cardRef.current.contains(event.target)) {
        closeVideo();
      }
    };

    window.addEventListener("click", handleOutsideClick);

    return () => {
      window.removeEventListener("click", handleOutsideClick);
    };
  }, [showVideo]);


  const openVideo = async (defaultUrl, progress_code) => {
    const programItem = data_program.find(i => i.progress_code === progress_code);

    let fetched = await fetchPrioritizedVideos(progress_code);
    fetched = Array.isArray(fetched) ? fetched : [];

    console.group(`🔍 openVideo Debug – progress_code=${progress_code}`);
    console.log("📥 Fetched from Dynamo:", fetched);

    // --- helpers -------------------------------------------------
    const isUnlock = (v) => {
      const src = (v?.source || "").toLowerCase();
      const cat = (v?.category || "").toLowerCase();
      const status = (v?.status || "").toLowerCase();
      const type = (v?.type || "").toLowerCase();

      const result =
        src === "unlock" ||
        src === "unlocked" ||   // 👈 handle "unlocked"
        cat === "unlock" ||
        cat === "unlocked" ||
        status === "unlock" ||
        status === "unlocked" ||
        type === "unlock" ||
        type === "unlocked" ||
        v?.unlock === true ||
        v?.isUnlock === true;

      console.log(
        `%c🔎 isUnlock?`,
        `color:${result ? "limegreen" : "red"}`,
        v,
        "→",
        result
      );

      return result;
    };

    const isDefaultByUrl = (v) => {
      if (!defaultUrl || !v?.url) return false;
      const embeddedDefault = toYouTubeEmbed(defaultUrl) || defaultUrl;
      const result = v.url === defaultUrl || v.url === embeddedDefault;

      console.log(
        `%c🎯 isDefaultByUrl?`,
        `color:${result ? "orange" : "gray"}`,
        { defaultUrl, embeddedDefault, videoUrl: v.url },
        "→",
        result
      );

      return result;
    };

    // --- classify each video -------------------------------------------------
    console.log("🧪 Classifying fetched videos…");
    fetched.forEach((v, i) => {
      console.log(`%cVideo #${i}`, "color:cyan", v);
      isUnlock(v);
      isDefaultByUrl(v);
    });

    let videos = [...fetched];

    const unlocks = videos.filter(isUnlock);
    const others = videos.filter((v) => !isUnlock(v));

    console.log("🔓 unlocks:", unlocks);
    console.log("📦 others:", others);

    let finalVideos = [];

    // ---------------------------------------------------------
    // CASE 1: UNLOCKS EXIST → NO DEFAULT ALLOWED
    // ---------------------------------------------------------
    if (unlocks.length > 0) {
      console.log("%c🚨 UNLOCK FOUND → default will be REMOVED", "color:yellow");
      const cleanedOthers = others.filter(
        (v) => !isDefaultByUrl(v) && v.source !== "default"
      );
      finalVideos = [...unlocks, ...cleanedOthers];

      console.log("👉 finalVideos (unlock mode):", finalVideos);
    }

    // ---------------------------------------------------------
    // CASE 2: NO UNLOCKS → default allowed
    // ---------------------------------------------------------
    else {
      console.log(
        "%cℹ️ No unlocks detected → default may be included",
        "color:lightblue"
      );

      let defaultVideo = null;
      if (defaultUrl) {
        const yt = toYouTubeEmbed(defaultUrl);
        defaultVideo = yt
          ? {
            url: yt,
            kind: "youtube",
            username: "Program Default",
            date: "",
            source: "default",
          }
          : looksLikeVideoFilePath(defaultUrl)
            ? {
              url: defaultUrl,
              kind: "file",
              username: "Program Default",
              date: "",
              source: "default",
            }
            : null;
      }

      const nonDefaultOthers = others.filter(
        (v) => !isDefaultByUrl(v) && v.source !== "default"
      );

      if (nonDefaultOthers.length === 0) {
        console.log("🟦 No others → just default");
        if (defaultVideo) finalVideos = [defaultVideo];
      } else {
        console.log(
          "🟩 No unlocks but others exist → default first, then others"
        );
        finalVideos = defaultVideo
          ? [defaultVideo, ...nonDefaultOthers]
          : [...nonDefaultOthers];
      }

      console.log("👉 finalVideos (no-unlock mode):", finalVideos);
    }

    console.groupEnd();

    if (!finalVideos.length) {
      setShowVideo(false);
      return;
    }

    setCarouselVideos(finalVideos);
    setCurrentVideoIndex(0);
    setCurrentTitle(`Level - ${programItem?.progression ?? ""}`);
    setShowVideo(true);
  };


  const closeVideo = () => {
    setShowVideo(false);
  };


  const calculateRowspan = (data) => {
    const rowspanData = {};
    data.forEach((item) => {
      const { level } = item;
      if (!rowspanData[level]) {
        rowspanData[level] = 1;
      } else {
        rowspanData[level] += 1;
      }
    });
    return rowspanData;
  };

  const rowspanData = calculateRowspan(data_program);


  return (
    <div className={styles.card} ref={cardRef}>
      <div className={styles.cardHeader}>
        <img src={headerImage} alt="Header" className={styles.headerImage} />
        <div>
          <span>Handstand Pushup</span>
          <p className={styles.subtitle}>6 Levels - 18 Progressions</p>
        </div>
        <div className={styles.downloadButton} onClick={handleCapture}>
          <FaDownload />
        </div>
      </div>
      <table className={styles.cardTable}>
        <tbody>
          {/* HEADERS 
        <tr>
            <th>Level</th>
            <th>Time</th> 
            <th>Progression</th>
          </tr>*/}
          {data_program.map((data, index) => {
            return (
              <tr key={index} className={`${styles.level}-${index + 1}`}>
                {index % rowspanData[data.level] === 0 && (
                  <td
                    rowSpan={rowspanData[data.level]}
                    className={`${styles.levelColumn} ${styles.levelCell}`}
                    style={{
                      backgroundColor: data.color,
                      color: data.color === "white" ? "black" : "white",
                    }}
                  >
                    <img
                      src={levelBadges[parseInt(data.level.replace("Level ", ""))]} // Extract the numeric part
                      alt={`Level ${data.level} Badge`}
                      className={styles.badgeImage}
                    />
                    {data.level}
                  </td>
                )}
                <td
                  className={`${styles.levelCell} ${styles.progressionCell}`}
                  style={{
                    background: data.color,
                    color: data.color === "white" ? "black" : "white",
                    textAlign: "left",
                  }}
                  onClick={() => openVideo(data.videoUrl, data.progress_code)}
                >
                  <span>{data.progression}</span>
                  <button
                    className={styles.watchVideoButton}
                    style={{
                      color: data.color === "white" ? "black" : "white",
                      float: "right",
                    }}
                  >
                    <BsFillPlayBtnFill className={styles.watchVideoIcon} />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <ModalVideo
        show={showVideo}
        videos={carouselVideos}
        currentIndex={currentVideoIndex}
        setCurrentIndex={setCurrentVideoIndex}
        onClose={closeVideo}
        currentTitle={currentTitle}
      />
    </div>
  );
}

export default CardHSPUProgram;
